import { useMemo, useState } from 'react';
import {
	Alert,
	AlertDescription,
	AlertIcon,
	AlertTitle,
	Box,
	Button,
	Flex,
	HStack,
	Icon,
	Input,
	Link,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Text,
	Tr,
	VStack,
	useDisclosure,
	useToast,
} from '@chakra-ui/react';
import {
	TbChevronRight,
	TbCheck,
	TbDownload,
	TbExternalLink,
	TbEye,
	TbEyeOff,
	TbMail,
	TbMailForward,
} from 'react-icons/tb';
import type { ElementType } from 'react';
import axios from 'axios';
import { QueryObserverResult, RefetchOptions } from '@tanstack/react-query';
import API_URL from '../../constants/environment';
import { ITSetupSection, LocationEntry } from '../../hooks/useGetSettings';
import { useGetLatestDownload } from '../../hooks/useGetLatestDownload';
import { ConfirmDialog, Notice } from '../../ui';
import CopyButton from '../CopyButton';
import {
	PARTBAY_BLUE,
	SettingsCard,
	SettingsChoiceCards,
	SettingsSubTable,
	SettingsTd,
	SettingsTh,
	useSubTablePagination,
} from './primitives';

const PYLON_CREATE_EMAIL_URL = 'https://pb-knowledge-base.help.usepylon.com/articles/4288700889-Creating-a-Company-Issued-Email';
const PYLON_FORWARDING_RULE_URL = 'https://pb-knowledge-base.help.usepylon.com/articles/9960719243-Email-Forwarding-Rule:-Microsoft-Exchange';

interface ITSetupProps {
	data: ITSetupSection['data'];
	locations: Array<LocationEntry>;
	refetch: (options?: RefetchOptions) => Promise<QueryObserverResult<any, Error>>;
}

type SetupType = 'single-server' | 'multi-server' | 'no-server';

const SETUP_TYPE_CHOICES = [
	{
		value: 'single-server' as SetupType,
		label: 'Single server',
		description: 'One server handles all locations. One set of credentials.',
		iconName: 'server' as const,
	},
	{
		value: 'multi-server' as SetupType,
		label: 'Multi server',
		description: 'Each location has its own server. Separate credentials per location.',
		iconName: 'network' as const,
	},
	{
		value: 'no-server' as SetupType,
		label: 'No server',
		description: 'Each user pushes EMS from their own device. No shared server accounts.',
		iconName: 'laptop' as const,
	},
];

type ServerSetupType = 'single-server' | 'multi-server';

/**
 * One of the three setup steps, as a full-width row inside the steps card.
 * Same treatment as a connection row on the Connections page: small icon tile,
 * label, live status underneath, chevron on the right.
 */
const StepRow = ({
	icon,
	label,
	status,
	isComplete = false,
	isDisabled = false,
	onClick,
}: {
	icon: ElementType;
	label: string;
	status: string;
	isComplete?: boolean;
	isDisabled?: boolean;
	onClick?: () => void;
}) => (
	<Flex
		as="button"
		type="button"
		w="100%"
		textAlign="left"
		align="center"
		gap={3}
		px={5}
		py={3.5}
		role="group"
		transition="background 0.15s"
		opacity={isDisabled ? 0.6 : 1}
		cursor={isDisabled ? 'not-allowed' : 'pointer'}
		_hover={isDisabled ? undefined : { bg: 'gray.50' }}
		onClick={isDisabled ? undefined : onClick}
		aria-disabled={isDisabled || undefined}
	>
		<Flex
			boxSize="28px"
			borderRadius="md"
			align="center"
			justify="center"
			flexShrink={0}
			bg={isComplete ? 'green.50' : 'gray.100'}
			color={isComplete ? 'green.600' : 'gray.600'}
		>
			<Icon as={isComplete ? (TbCheck as ElementType) : icon} boxSize={3.5} />
		</Flex>
		<Box flex={1} minW={0}>
			<Text fontSize="sm" fontWeight="medium" color="gray.800">
				{label}
			</Text>
			<Text
				fontSize="sm"
				color={isComplete ? 'green.600' : 'gray.500'}
				fontWeight={isComplete ? 'medium' : 'normal'}
				mt={0.5}
				noOfLines={1}
			>
				{status}
			</Text>
		</Box>
		<Icon
			as={TbChevronRight as ElementType}
			boxSize={4}
			color="gray.400"
			flexShrink={0}
			_groupHover={{ color: isDisabled ? 'gray.400' : 'gray.600' }}
		/>
	</Flex>
);

/** Label above a copyable value, for the values a user pastes into Exchange. */
const ForwardingValue = ({ label, value }: { label: string; value: string | null }) => (
	<Box borderWidth="1px" borderColor="gray.200" borderRadius="md" px={4} py={3}>
		<Text
			fontSize="11px"
			fontWeight="semibold"
			color="gray.500"
			letterSpacing="wider"
			textTransform="uppercase"
			mb={1}
		>
			{label}
		</Text>
		{value ? (
			<HStack spacing={1} minW={0}>
				<Text fontSize="sm" fontFamily="mono" color="gray.700" wordBreak="break-all">
					{value}
				</Text>
				<CopyButton text={value} label={`Copy ${label}`} />
			</HStack>
		) : (
			<Text fontSize="sm" color="gray.400" fontStyle="italic">
				Not set
			</Text>
		)}
	</Box>
);

const ITSetup = ({ data, locations, refetch }: ITSetupProps) => {
	const [showPasswords, setShowPasswords] = useState(false);
	const [selectedSetupType, setSelectedSetupType] = useState<SetupType | null>(null);
	const [isGenerating, setIsGenerating] = useState(false);
	const [isAddingMissing, setIsAddingMissing] = useState(false);
	const toast = useToast();

	// Drive the lower half of the card based on what's actually missing for the current setup type.
	// `unconfigured` keeps the original picker; the other states surface a recovery CTA so users
	// (e.g. when an account was deleted, generation half-failed, or a location was added later)
	// never end up looking at an empty card with no path forward.
	const completionState = useMemo(() => {
		if (data.setupType === null) return { kind: 'unconfigured' as const };
		if (data.setupType === 'no-server') return { kind: 'no-server' as const };

		if (data.setupType === 'single-server') {
			const hasAccount = data.serverAccounts.some((a) => a.locationId === 'all');
			return hasAccount
				? { kind: 'complete' as const }
				: { kind: 'single-missing' as const };
		}

		if (locations.length === 0) {
			return data.serverAccounts.length === 0
				? { kind: 'multi-no-locations' as const }
				: { kind: 'complete' as const };
		}

		const accountedFor = new Set(
			data.serverAccounts
				.filter((a) => a.locationId !== 'all')
				.map((a) => String(a.locationId))
		);
		const missing = locations.filter((loc) => !accountedFor.has(String(loc._id)));
		return missing.length > 0
			? { kind: 'multi-missing' as const, missing }
			: { kind: 'complete' as const };
	}, [data.serverAccounts, data.setupType, locations]);

	const emailModal = useDisclosure();
	const forwardingModal = useDisclosure();
	const legacyDownloadConfirm = useDisclosure();
	const [emailForm, setEmailForm] = useState({ email: '' });
	const [isProvisioning, setIsProvisioning] = useState(false);

	const emailProvisioned = !!data.inboundEmail;

	const { currentPage, setCurrentPage, totalPages, pageItems } = useSubTablePagination(
		data.serverAccounts
	);

	const { mutate: fetchLatestDownload, isPending: isDownloadPending } = useGetLatestDownload();
	const [downloadingLegacy, setDownloadingLegacy] = useState<boolean | null>(null);

	const startDownload = (legacy: boolean) => {
		if (isDownloadPending) return;
		setDownloadingLegacy(legacy);
		fetchLatestDownload(
			{ legacy },
			{
				onSuccess: ({ downloadUrl }) => {
					if (legacy) legacyDownloadConfirm.onClose();
					window.location.href = downloadUrl;
				},
				onError: () => {
					toast({
						title: 'Download failed',
						description: 'Could not retrieve the latest installer. Please try again.',
						status: 'error',
						duration: 5000,
						isClosable: true,
					});
				},
				onSettled: () => {
					setDownloadingLegacy(null);
				},
			},
		);
	};

	const handleDownloadApp = (legacy: boolean) => {
		if (isDownloadPending) return;
		if (legacy) {
			legacyDownloadConfirm.onOpen();
			return;
		}
		startDownload(false);
	};

	const generateServerAccounts = async (setupType: ServerSetupType, description: string) => {
		const sessionId = localStorage.getItem('session_id');
		await axios.post(
			`${API_URL}/settings/account/it-setup`,
			{ action: 'generate-server-accounts', setupType },
			{ headers: { Authorization: sessionId ?? '' } }
		);
		toast({
			title: 'Server accounts created',
			description,
			status: 'success',
			duration: 5000,
			isClosable: true,
		});
		await refetch();
	};

	const saveNoServerSetup = async () => {
		const sessionId = localStorage.getItem('session_id');
		await axios.post(
			`${API_URL}/settings/account/it-setup`,
			{ action: 'set-setup-type', setupType: 'no-server' },
			{ headers: { Authorization: sessionId ?? '' } }
		);
		toast({
			title: 'Setup saved',
			description: 'Users will push EMS from their own devices. Server accounts will not be created.',
			status: 'success',
			duration: 5000,
			isClosable: true,
		});
		await refetch();
	};

	const handleGenerateAccounts = async () => {
		if (!selectedSetupType || selectedSetupType === 'no-server') return;
		setIsGenerating(true);
		try {
			await generateServerAccounts(
				selectedSetupType,
				'Credentials have been generated and are ready for use.'
			);
		} catch {
			toast({
				title: 'Failed to generate accounts',
				description: 'Could not create server accounts. Please try again.',
				status: 'error',
				duration: 4000,
				isClosable: true,
			});
		} finally {
			setIsGenerating(false);
		}
	};

	const handleConfirmSetup = async () => {
		if (selectedSetupType === 'no-server') {
			setIsGenerating(true);
			try {
				await saveNoServerSetup();
			} catch {
				toast({
					title: 'Failed to save setup',
					description: 'Could not save no-server setup. Please try again.',
					status: 'error',
					duration: 4000,
					isClosable: true,
				});
			} finally {
				setIsGenerating(false);
			}
			return;
		}
		await handleGenerateAccounts();
	};

	const handleCreateMissingServerAccounts = async () => {
		if (data.setupType !== 'single-server' && data.setupType !== 'multi-server') return;
		setIsAddingMissing(true);
		try {
			await generateServerAccounts(
				data.setupType,
				completionState.kind === 'multi-missing'
					? `Created credentials for ${completionState.missing.length} location${
						completionState.missing.length === 1 ? '' : 's'
					}.`
					: 'Credentials have been generated and are ready for use.'
			);
		} catch {
			toast({
				title: 'Failed to create accounts',
				description: 'Could not create server accounts. Please try again.',
				status: 'error',
				duration: 4000,
				isClosable: true,
			});
		} finally {
			setIsAddingMissing(false);
		}
	};

	const handleProvisionEmails = async () => {
		if (!emailForm.email.trim()) {
			toast({ title: 'Email address is required', status: 'warning', duration: 3000 });
			return;
		}
		setIsProvisioning(true);
		try {
			const sessionId = localStorage.getItem('session_id');
			await axios.post(
				`${API_URL}/settings/account/it-setup`,
				{
					action: 'provision-org-emails',
					clientPartstraderEmail: emailForm.email,
					force: true,
				},
				{ headers: { Authorization: sessionId ?? '' } }
			);
			toast({
				title: 'Emails provisioned successfully',
				description: 'Your Yaxle admin mailbox and inbound email have been created.',
				status: 'success',
				duration: 5000,
				isClosable: true,
			});
			await refetch();
			emailModal.onClose();
		} catch {
			toast({
				title: 'Failed to provision emails',
				description: 'Could not create Yaxle emails. Please try again.',
				status: 'error',
				duration: 4000,
				isClosable: true,
			});
		} finally {
			setIsProvisioning(false);
		}
	};

	return (
		<VStack spacing={6} align="stretch" pb={16}>
			<SettingsCard
				title="Setup"
				description="Install the desktop app, then route PartsTrader email into Yaxle."
				isFlush
			>
				<StepRow
					icon={TbDownload as ElementType}
					label="Download app"
					status={
						isDownloadPending && downloadingLegacy === false
							? 'Preparing the latest installer…'
							: 'Get the Yaxle desktop application'
					}
					isDisabled={isDownloadPending}
					onClick={() => handleDownloadApp(false)}
				/>
				<StepRow
					icon={TbDownload as ElementType}
					label="Download legacy app"
					status={
						isDownloadPending && downloadingLegacy === true
							? 'Preparing the legacy installer…'
							: 'Deprecated — previous desktop application'
					}
					isDisabled={isDownloadPending}
					onClick={() => handleDownloadApp(true)}
				/>
				<StepRow
					icon={TbMail as ElementType}
					label="Create an email"
					isComplete={emailProvisioned}
					status={
						emailProvisioned
							? (data.clientPartstraderEmail ?? 'Email provisioned')
							: 'Set up a dedicated email for Yaxle'
					}
					onClick={emailModal.onOpen}
				/>
				<StepRow
					icon={TbMailForward as ElementType}
					label="Setup inbound rule"
					isDisabled={!emailProvisioned}
					status={
						emailProvisioned
							? 'Forward PartsTrader notifications to Yaxle'
							: 'Complete email setup first'
					}
					onClick={forwardingModal.onOpen}
				/>
			</SettingsCard>

			<SettingsCard
				title="Server accounts"
				description="Login credentials for the Yaxle desktop application"
				headerAction={
					data.serverAccounts.length > 0 ? (
						<Button
							size="xs"
							variant="ghost"
							color="gray.500"
							_hover={{ bg: 'gray.100', color: 'gray.700' }}
							leftIcon={
								<Icon
									as={(showPasswords ? TbEyeOff : TbEye) as ElementType}
									boxSize={3.5}
								/>
							}
							onClick={() => setShowPasswords(!showPasswords)}
						>
							{showPasswords ? 'Hide' : 'Show'} passwords
						</Button>
					) : undefined
				}
				isFlush
			>
				{completionState.kind === 'unconfigured' ? (
					<VStack spacing={4} align="stretch" px={5} py={4}>
						<Text fontSize="sm" color="gray.500">
							Choose how the Yaxle desktop application will be installed at your
							organization.
						</Text>
						<SettingsChoiceCards
							choices={SETUP_TYPE_CHOICES}
							// Nothing is preselected, so no card reads as chosen until
							// the user picks one.
							value={(selectedSetupType ?? '') as SetupType}
							onChange={setSelectedSetupType}
						/>
						<Button
							size="action"
							colorScheme="partbay-blue"
							alignSelf="flex-end"
							isDisabled={!selectedSetupType}
							isLoading={isGenerating}
							loadingText={selectedSetupType === 'no-server' ? 'Saving…' : 'Generating…'}
							onClick={handleConfirmSetup}
						>
							{selectedSetupType === 'no-server'
								? 'Save setup'
								: 'Generate server accounts'}
						</Button>
					</VStack>
				) : (
					<>
						{completionState.kind === 'no-server' && (
							<Box px={5} py={4}>
								<Notice title="No-server setup">
									Each user pushes EMS from their own device. Server accounts are
									not used for this organization.
								</Notice>
							</Box>
						)}
						{completionState.kind === 'single-missing' && (
							<Box px={5} py={4}>
								<Alert status="warning" borderRadius="md" alignItems="flex-start">
									<AlertIcon boxSize={4} mt={0.5} />
									<HStack justify="space-between" align="flex-start" gap={4} flex={1}>
										<Box>
											<AlertTitle fontSize="sm">
												Server account not yet created
											</AlertTitle>
											<AlertDescription fontSize="sm" color="gray.600">
												This organization is configured for single-server, but the
												shared server account hasn't been generated yet.
											</AlertDescription>
										</Box>
										<Button
											size="sm"
											colorScheme="partbay-blue"
											flexShrink={0}
											isLoading={isAddingMissing}
											loadingText="Creating…"
											onClick={handleCreateMissingServerAccounts}
										>
											Create account
										</Button>
									</HStack>
								</Alert>
							</Box>
						)}
						{completionState.kind === 'multi-missing' && (
							<Box px={5} py={4}>
								<Alert status="warning" borderRadius="md" alignItems="flex-start">
									<AlertIcon boxSize={4} mt={0.5} />
									<HStack justify="space-between" align="flex-start" gap={4} flex={1}>
										<Box minW={0}>
											<AlertTitle fontSize="sm">
												{completionState.missing.length} location
												{completionState.missing.length === 1 ? '' : 's'} missing a
												server account
											</AlertTitle>
											<AlertDescription fontSize="sm" color="gray.600">
												{completionState.missing.map((l) => l.title).join(', ')}
											</AlertDescription>
										</Box>
										<Button
											size="sm"
											colorScheme="partbay-blue"
											flexShrink={0}
											isLoading={isAddingMissing}
											loadingText="Creating…"
											onClick={handleCreateMissingServerAccounts}
										>
											Create {completionState.missing.length === 1 ? 'account' : 'accounts'}
										</Button>
									</HStack>
								</Alert>
							</Box>
						)}
						{completionState.kind === 'multi-no-locations' && (
							<Box px={5} py={4}>
								<Alert status="info" borderRadius="md" alignItems="flex-start">
									<AlertIcon boxSize={4} mt={0.5} />
									<Box>
										<AlertTitle fontSize="sm">No locations yet</AlertTitle>
										<AlertDescription fontSize="sm" color="gray.600">
											This organization is configured for multi-server, but no
											locations have been added yet. Add a location in the Locations
											section to generate its server account.
										</AlertDescription>
									</Box>
								</Alert>
							</Box>
						)}
						{data.serverAccounts.length > 0 && (
							<SettingsSubTable
								isEmbedded
								head={
									<>
										<SettingsTh>Location</SettingsTh>
										<SettingsTh>Username</SettingsTh>
										<SettingsTh>Password</SettingsTh>
									</>
								}
								currentPage={currentPage}
								totalPages={totalPages}
								onPageChange={setCurrentPage}
								footerLeft={
									<Text fontSize="xs" color="gray.500">
										{data.serverAccounts.length} account
										{data.serverAccounts.length === 1 ? '' : 's'}
									</Text>
								}
							>
								{pageItems.map((account) => (
									<Tr key={`${account.locationId}-${account.email}`}>
										<SettingsTd fontWeight="medium" color="gray.800">
											{account.locationName}
										</SettingsTd>
										<SettingsTd>
											<HStack spacing={1} minW={0}>
												<Text
													fontSize="sm"
													fontFamily="mono"
													color="gray.700"
													wordBreak="break-all"
												>
													{account.email}
												</Text>
												<CopyButton text={account.email} label="Copy username" />
											</HStack>
										</SettingsTd>
										<SettingsTd>
											<HStack spacing={1} minW={0}>
												<Text
													fontSize="sm"
													fontFamily="mono"
													color="gray.700"
													wordBreak="break-all"
												>
													{showPasswords
														? account.password
														: '•'.repeat(Math.min(account.password.length, 12))}
												</Text>
												<CopyButton text={account.password} label="Copy password" />
											</HStack>
										</SettingsTd>
									</Tr>
								))}
							</SettingsSubTable>
						)}
					</>
				)}
			</SettingsCard>

			<ConfirmDialog
				isOpen={legacyDownloadConfirm.isOpen}
				onClose={legacyDownloadConfirm.onClose}
				onConfirm={() => startDownload(true)}
				title="Download the legacy app?"
				confirmLabel="Download legacy app"
				isConfirmLoading={isDownloadPending && downloadingLegacy === true}
			>
				This is the previous desktop application. Performance may be degraded.  
			</ConfirmDialog>

			{/* Create Email Modal */}
			<Modal isOpen={emailModal.isOpen} onClose={emailModal.onClose} size="lg" isCentered>
				<ModalOverlay />
				<ModalContent>
					<ModalHeader fontSize="md" fontWeight="semibold" color="gray.800" py={4}>
						{emailProvisioned ? 'Email setup complete' : 'Create a dedicated email'}
					</ModalHeader>
					<ModalCloseButton top={3} />
					<ModalBody>
						{emailProvisioned ? (
							<VStack spacing={3} align="stretch">
								<Text fontSize="sm" color="gray.500">
									These mailboxes are provisioned and routing into Yaxle.
								</Text>
								<ForwardingValue
									label="Client email"
									value={data.clientPartstraderEmail}
								/>
								<ForwardingValue label="Admin mailbox" value={data.adminEmail} />
								<ForwardingValue label="Inbound email" value={data.inboundEmail} />
							</VStack>
						) : (
							<VStack spacing={4} align="stretch">
								<Text fontSize="sm" color="gray.500">
									Follow the instructions in the article below to create a dedicated
									email in your Microsoft 365 admin center, then enter the credentials
									here.
								</Text>

								<Link
									href={PYLON_CREATE_EMAIL_URL}
									isExternal
									fontSize="sm"
									fontWeight="medium"
									color={PARTBAY_BLUE}
								>
									View instructions: Creating a Company Issued Email
									<Icon as={TbExternalLink as ElementType} ml={1} boxSize={3.5} />
								</Link>

								<Box borderTopWidth="1px" borderColor="gray.200" pt={4}>
									<Text fontSize="sm" fontWeight="medium" color="gray.800" mb={2}>
										Enter the email account you created
									</Text>
									<Input
										size="sm"
										placeholder="e.g. shopname-central-parts@yourdomain.com"
										value={emailForm.email}
										onChange={(e) =>
											setEmailForm((f) => ({ ...f, email: e.target.value }))
										}
									/>
								</Box>
							</VStack>
						)}
					</ModalBody>
					<ModalFooter>
						{emailProvisioned ? (
							<Button size="action" colorScheme="partbay-blue" onClick={emailModal.onClose}>
								Done
							</Button>
						) : (
							<>
								<Button
									size="action"
									variant="outlineGray"
									mr={3}
									onClick={emailModal.onClose}
									isDisabled={isProvisioning}
								>
									Cancel
								</Button>
								<Button
									size="action"
									colorScheme="partbay-blue"
									isLoading={isProvisioning}
									loadingText="Provisioning…"
									onClick={handleProvisionEmails}
								>
									Confirm &amp; provision
								</Button>
							</>
						)}
					</ModalFooter>
				</ModalContent>
			</Modal>

			{/* Forwarding Rule Modal */}
			<Modal isOpen={forwardingModal.isOpen} onClose={forwardingModal.onClose} size="lg" isCentered>
				<ModalOverlay />
				<ModalContent>
					<ModalHeader fontSize="md" fontWeight="semibold" color="gray.800" py={4}>
						Setup inbound forwarding rule
					</ModalHeader>
					<ModalCloseButton top={3} />
					<ModalBody>
						<VStack spacing={4} align="stretch">
							<Text fontSize="sm" color="gray.500">
								Follow the instructions in the article below to create an email
								forwarding rule in your Microsoft Exchange Admin Center using the values
								provided.
							</Text>

							<Link
								href={PYLON_FORWARDING_RULE_URL}
								isExternal
								fontSize="sm"
								fontWeight="medium"
								color={PARTBAY_BLUE}
							>
								View instructions: Email Forwarding Rule (Microsoft Exchange)
								<Icon as={TbExternalLink as ElementType} ml={1} boxSize={3.5} />
							</Link>

							<VStack spacing={3} align="stretch" borderTopWidth="1px" borderColor="gray.200" pt={4}>
								<ForwardingValue
									label="Recipient condition"
									value={data.clientPartstraderEmail}
								/>
								<ForwardingValue label="Redirect target" value={data.inboundEmail} />
								<ForwardingValue label="Sender domain" value="partstrader.us.com" />
							</VStack>
						</VStack>
					</ModalBody>
					<ModalFooter>
						<Button size="action" colorScheme="partbay-blue" onClick={forwardingModal.onClose}>
							Done
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</VStack>
	);
};

export default ITSetup;
